import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, Feather } from "lucide-react";
import { ARTICLES } from "@/data/articles";
import { LogoMark } from "@/components/Navbar";
import { BookingModal } from "@/components/BookingModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(ARTICLES.find((a) => a.slug === slug) || null);
  const [bookOpen, setBookOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/articles/${slug}`)
      .then((r) => setArticle(r.data))
      .catch(() => {});
  }, [slug]);

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-5 text-center" data-testid="article-not-found">
        <p className="font-serif text-2xl font-semibold text-pine-950">Article not found</p>
        <Link to="/" className="mt-4 text-sm font-semibold text-clay underline-offset-4 hover:underline">
          ← Back to home
        </Link>
      </div>
    );
  }

  const hasBody = Array.isArray(article.body) && article.body.length > 0;

  return (
    <div className="min-h-screen bg-cream text-pine-900" data-testid="article-page">
      <header className="border-b border-pine-900/10">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="article-brand">
            <LogoMark />
            <span className="leading-none">
              <span className="block font-serif text-lg font-semibold tracking-tight">SpellingBee</span>
              <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.22em] text-pine-700/70">Therapy Services</span>
            </span>
          </Link>
          <button
            data-testid="article-book-button"
            onClick={() => setBookOpen(true)}
            className="rounded-full bg-pine-900 px-5 py-2.5 text-xs font-semibold text-cream transition-colors hover:bg-pine-800"
          >
            Book a free consult
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-12">
        <Link
          to="/"
          data-testid="article-back-link"
          className="flex w-fit items-center gap-1.5 text-sm font-semibold text-pine-700 transition-colors hover:text-clay"
        >
          <ArrowLeft size={15} /> Back to journal
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full border border-clay/30 bg-clay/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-clay">
              {article.tag}
            </span>
            <span className="flex items-center gap-1.5 font-mono text-[11px] text-stone-500">
              <Clock size={12} /> {article.read}
            </span>
          </div>

          <h1 className="mt-6 font-serif text-3xl font-semibold leading-tight tracking-tight text-pine-950 sm:text-4xl" data-testid="article-title">
            {article.title}
          </h1>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">
            By {article.author || "Sameeksha Shetty"} · RCI-Registered Speech-Language Pathologist
          </p>

          <p className="mt-10 border-l-4 border-honey pl-6 font-serif text-lg italic leading-relaxed text-pine-900 sm:text-xl" data-testid="article-lede">
            {article.excerpt}
          </p>

          {hasBody ? (
            <div className="mt-10 space-y-9" data-testid="article-body">
              {article.body.map((section, i) => (
                <section key={i}>
                  {section.heading && (
                    <h2 className="font-serif text-xl font-semibold text-pine-950 sm:text-2xl">{section.heading}</h2>
                  )}
                  {(section.paragraphs || []).map((p, j) => (
                    <p key={j} className="mt-4 text-base leading-relaxed text-stone-600">{p}</p>
                  ))}
                </section>
              ))}
            </div>
          ) : (
            <div
              data-testid="article-coming-soon"
              className="mt-10 flex items-start gap-4 rounded-2xl border border-dashed border-pine-900/20 bg-sand p-6"
            >
              <Feather size={18} className="mt-0.5 shrink-0 text-clay" />
              <p className="text-sm leading-relaxed text-stone-600">
                Sameeksha is writing the full article — it will appear here soon.
                In the meantime, if this topic sounds familiar, a free consult is
                the fastest way to get answers about your child or your school.
              </p>
            </div>
          )}

          <div className="mt-14 rounded-3xl bg-pine-950 p-8 text-cream sm:p-10" data-testid="article-cta">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-honey">Not sure where to start?</p>
            <p className="mt-3 font-serif text-xl font-semibold sm:text-2xl">
              A free 15-minute consult is the easiest first step.
            </p>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-cream/65">
              Share what's on your mind — a child, a class, or a whole school —
              and Sameeksha will tell you honestly whether therapy would help.
            </p>
            <button
              data-testid="article-cta-book-button"
              onClick={() => setBookOpen(true)}
              className="group mt-6 flex items-center gap-2 rounded-full bg-honey px-7 py-3.5 text-sm font-semibold text-pine-950 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#ffd95e]"
            >
              Book a free consult
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </motion.article>
      </main>

      <BookingModal open={bookOpen} onClose={() => setBookOpen(false)} />
    </div>
  );
}
